import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import type { District } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

/**
 * ストリートビュー机上調査の地区設定 panel (docs/25 §10-1)。マスター管理者専用。
 *
 * 3つの設定を扱う (すべて master のみ編集可、firestore.rules の地区管理者 update 許可フィールドに
 * `streetView` を含めないことで書込保護):
 *   1. analysisEnabled — この地区で机上調査を実施可能にするか (地区別の実施可否ゲート、既定 false)。
 *      OFF のとき、この地区では解析 callable が全拒否され、机上調査の画面も使えない。
 *   2. districtAdminCanAnalyze — 実施可能にした場合に、地区管理者にも分析の実行 (課金) を許可するか。
 *      既定 false = 実行はマスターのみ、地区管理者は結果閲覧のみ。
 *   3. enabled — 3D機能 (道路方向の自動検出 + 3Dパノラマへの結果重畳)。Street View 表示課金を伴う。
 *
 * Street View 解析・3D表示ともに Google Maps 表示課金 (Gemini/Static) を伴うため、他の AI 系設定
 * (既定 enabled=true) とは非対称に、いずれも既定 false (opt-in) とする。
 */

export interface DistrictStreetViewPanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictStreetViewPanel({ district, uid, canEdit }: DistrictStreetViewPanelProps) {
  const { toast } = useToast();
  const [analysisEnabled, setAnalysisEnabled] = useState<boolean>(
    district.streetView?.analysisEnabled === true,
  );
  const [districtAdminCanAnalyze, setDistrictAdminCanAnalyze] = useState<boolean>(
    district.streetView?.districtAdminCanAnalyze === true,
  );
  const [threeDEnabled, setThreeDEnabled] = useState<boolean>(
    district.streetView?.enabled === true,
  );
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!canEdit) return;
    setSaving(true);
    try {
      const ref = doc(db, 'districts', district.id);
      await setDoc(
        ref,
        {
          streetView: {
            analysisEnabled,
            // 実施不可のときは下位設定は意味を持たないが、保存値としては現状のトグル状態を尊重する
            // (再度 ON にしたとき設定が保たれる)。ゲート判定は analysisEnabled が優先されるため安全。
            districtAdminCanAnalyze,
            enabled: threeDEnabled,
          },
          updatedAt: serverTimestamp(),
          updatedBy: uid,
        },
        { merge: true },
      );
      toast('success', 'ストリートビュー机上調査の設定を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-street-view-panel">
      <h3 className="text-base font-bold text-slate-900 mb-3">🗺️ ストリートビュー机上調査</h3>
      <p className="text-xs text-slate-500 mb-3">
        座標を指定して Street View を AI
        解析する「机上調査」を、この地区で使えるようにするか設定します。
        解析は画像取得とAIの利用に課金が発生するため、マスター管理者が地区ごとに許可します。
      </p>

      <label className="flex items-start gap-2 mb-3">
        <input
          type="checkbox"
          checked={analysisEnabled}
          onChange={(e) => setAnalysisEnabled(e.target.checked)}
          disabled={!canEdit || saving}
          className="h-4 w-4 mt-0.5"
          data-testid="street-view-analysis-enabled"
        />
        <span className="text-sm">この地区でストリートビュー机上調査を実施可能にする</span>
      </label>

      {analysisEnabled && (
        <div className="ml-6 border-l-2 border-slate-100 pl-4 space-y-3 mb-3">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={districtAdminCanAnalyze}
              onChange={(e) => setDistrictAdminCanAnalyze(e.target.checked)}
              disabled={!canEdit || saving}
              className="h-4 w-4 mt-0.5"
              data-testid="street-view-district-admin-can-analyze"
            />
            <span className="text-sm">
              地区管理者にも分析の実行を許可する（実行のたびに課金が発生します）
              <span className="block text-xs text-slate-500">
                オフのときは、分析の実行はマスター管理者のみです（地区管理者は結果の閲覧のみ）。
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={threeDEnabled}
              onChange={(e) => setThreeDEnabled(e.target.checked)}
              disabled={!canEdit || saving}
              className="h-4 w-4 mt-0.5"
              data-testid="street-view-enabled"
            />
            <span className="text-sm">
              3D機能（道路方向の自動検出と3Dパノラマへの結果重畳）を有効化する
              <span className="block text-xs text-slate-500">
                Google Maps の表示課金が発生します。
              </span>
            </span>
          </label>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="street-view-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}
